import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Truck, ExternalLink, CheckCircle, AlertTriangle, Package, MapPin, ChevronDown, ChevronRight, ImageIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getImageUrl } from '../utils/imageUrl';
import { hasExpress } from '../utils/express';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TransportOrders() {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [trackingData, setTrackingData] = useState({});
  const [packageData, setPackageData] = useState({});
  const [confirmValidate, setConfirmValidate] = useState(null);
  const [addressDialog, setAddressDialog] = useState(null);
  const [expandedColis, setExpandedColis] = useState({});
  const [colisTrackingData, setColisTrackingData] = useState({});
  const [colisPackageData, setColisPackageData] = useState({});
  const [confirmValidateColis, setConfirmValidateColis] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/transport-orders`, { withCredentials: true });
      setOrders(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group orders into ungrouped + colis
  const ungrouped = [];
  const colisGroups = {};
  orders.forEach(o => {
    const pid = o.transport_package_id;
    if (pid) {
      if (!colisGroups[pid]) colisGroups[pid] = { orders: [], name: o.transport_package_name, weight: o.transport_package_weight, price: o.transport_package_price };
      colisGroups[pid].orders.push(o);
    } else {
      ungrouped.push(o);
    }
  });

  const validateTrackingLink = (link) => {
    if (!link || !link.trim()) return true; // empty is ok (optional)
    return link.trim().startsWith('http://') || link.trim().startsWith('https://');
  };

  const handleSaveTracking = async (orderId) => {
    const d = trackingData[orderId] || {};
    if (!validateTrackingLink(d.link)) {
      toast.error('Le lien tracking doit commencer par http:// ou https://');
      return;
    }
    try {
      await axios.put(`${API}/api/transport-orders/${orderId}/tracking`, {
        tracking_number: d.number ?? '',
        tracking_link: d.link ?? ''
      }, { withCredentials: true });
      toast.success(t('tracking_saved'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSaveColisTracking = async (pkgId) => {
    const d = colisTrackingData[pkgId] || {};
    if (!validateTrackingLink(d.link)) {
      toast.error('Le lien tracking doit commencer par http:// ou https://');
      return;
    }
    try {
      await axios.put(`${API}/api/transport/packages/${pkgId}/tracking`, {
        tracking_number: d.number ?? '',
        tracking_link: d.link ?? ''
      }, { withCredentials: true });
      toast.success('Tracking mis à jour sur le colis et ses commandes');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleValidatePackage = async () => {
    if (!confirmValidate) return;
    const d = packageData[confirmValidate] || {};
    if (!d.weight || !d.price) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.put(`${API}/api/transport-orders/${confirmValidate}/validate-package`, {
        package_weight: parseFloat(d.weight),
        package_price_vnd: parseFloat(d.price)
      }, { withCredentials: true });
      toast.success(t('package_validated'));
      setConfirmValidate(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleValidateColisPackage = async () => {
    if (!confirmValidateColis) return;
    const d = colisPackageData[confirmValidateColis] || {};
    if (!d.weight || !d.price) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.put(`${API}/api/transport/packages/${confirmValidateColis}/validate`, {
        weight: parseFloat(d.weight),
        price: parseFloat(d.price)
      }, { withCredentials: true });
      toast.success(t('package_validated'));
      setConfirmValidateColis(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Render a single order row
  const OrderRow = ({ o, isChild }) => {
    const hasTracking = !!(o.tracking_number?.trim() || o.tracking_link?.trim());
    return (
      <div data-testid={`transport-order-${o.order_number}`} className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 ${isChild ? 'border-blue-100 bg-blue-50/20 ml-8' : 'border-slate-100'}`}>
        <div className="flex-shrink-0">
          {o.images && o.images.length > 0 ? (
            <img src={getImageUrl(o.images[0])} alt="" className="w-12 h-12 object-cover rounded border border-slate-300" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
          ) : null}
          <div className={`w-12 h-12 bg-slate-200 rounded border border-slate-300 items-center justify-center ${o.images && o.images.length > 0 ? 'hidden' : 'flex'}`}>
            <ImageIcon size={16} className="text-slate-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm">{o.order_number}</span>
            {hasExpress(o) && <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
            <span className="text-sm text-slate-700">{o.client_name}</span>
            <Badge className="bg-slate-100 text-slate-600 text-xs">{o.client_country}</Badge>
          </div>
        </div>
        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setAddressDialog(o)}>
          <MapPin size={13} /> {t('address')}
        </Button>
        {!isChild && (
          <>
            {/* Tracking section */}
            {hasTracking ? (
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-slate-700">{o.tracking_number}</span>
                {o.tracking_link && (
                  <a href={o.tracking_link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"><ExternalLink size={10} /></Button>
                  </a>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Input className="h-7 w-24 text-xs" placeholder="N° tracking"
                  value={trackingData[o._id]?.number ?? ''}
                  onChange={e => setTrackingData(prev => ({ ...prev, [o._id]: { ...prev[o._id], number: e.target.value } }))} />
                <Input className="h-7 w-32 text-xs" placeholder="Lien (optionnel)"
                  value={trackingData[o._id]?.link ?? ''}
                  onChange={e => setTrackingData(prev => ({ ...prev, [o._id]: { ...prev[o._id], link: e.target.value } }))} />
                <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => handleSaveTracking(o._id)}>OK</Button>
              </div>
            )}
            {/* Weight/Price */}
            {o.transport_validated ? (
              <Badge className="bg-emerald-100 text-emerald-800 text-[10px] gap-1"><CheckCircle size={10} /> {o.package_weight}kg — {(o.package_price_vnd || 0).toLocaleString()}đ</Badge>
            ) : (
              <div className="flex items-center gap-1">
                <Input type="number" step="0.1" className="h-7 w-16 text-xs" placeholder="kg"
                  value={packageData[o._id]?.weight ?? ''}
                  onChange={e => setPackageData(prev => ({ ...prev, [o._id]: { ...prev[o._id], weight: e.target.value } }))} />
                <Input type="number" step="1000" className="h-7 w-24 text-xs" placeholder="VND"
                  value={packageData[o._id]?.price ?? ''}
                  onChange={e => setPackageData(prev => ({ ...prev, [o._id]: { ...prev[o._id], price: e.target.value } }))} />
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-0.5" onClick={() => setConfirmValidate(o._id)}>
                  <CheckCircle size={10} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Truck size={24} className="text-slate-700" />
          <h1 className="text-2xl font-semibold text-slate-900">{t('transport_orders_title')}</h1>
          <Badge className="bg-slate-100 text-slate-700">{orders.length}</Badge>
        </div>

        {orders.length === 0 ? (
          <Card className="border-slate-200"><CardContent className="py-12 text-center text-sm text-slate-500">{t('no_transport_orders')}</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {/* Ungrouped orders */}
            {ungrouped.map(o => <OrderRow key={o._id} o={o} />)}

            {/* Colis groups */}
            {Object.entries(colisGroups).map(([pkgId, group]) => {
              const isExp = expandedColis[pkgId];
              const firstOrder = group.orders[0];
              const colisTracking = group.orders.find(o => o.tracking_number?.trim() || o.tracking_link?.trim());
              const hasColisTracking = !!colisTracking;
              const colisTrackNum = colisTracking?.tracking_number || '';
              const colisTrackLink = colisTracking?.tracking_link || '';
              const colisValidated = group.weight > 0 && group.price > 0;

              return (
                <div key={pkgId} className="border border-blue-200 rounded-lg overflow-hidden">
                  {/* Colis header - same structure as OrderRow for alignment */}
                  <div className="flex items-center gap-3 p-3 bg-blue-50">
                    <div className="flex-shrink-0 w-12 flex items-center justify-center">
                      <button onClick={() => setExpandedColis(prev => ({ ...prev, [pkgId]: !prev[pkgId] }))}>
                        {isExp ? <ChevronDown size={16} className="text-blue-600" /> : <ChevronRight size={16} className="text-blue-600" />}
                      </button>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer" onClick={() => setExpandedColis(prev => ({ ...prev, [pkgId]: !prev[pkgId] }))}>
                      <Package size={16} className="text-blue-600" />
                      <span className="font-bold text-blue-800 text-sm">{group.name}</span>
                      <span className="text-xs text-blue-600">({group.orders.length} commandes)</span>
                    </div>
                    {/* Address button - aligned with orders */}
                    <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setAddressDialog(firstOrder)}>
                      <MapPin size={13} /> {t('address')}
                    </Button>
                    {/* Tracking - aligned with orders */}
                    {hasColisTracking ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-medium text-slate-700">{colisTrackNum}</span>
                        {colisTrackLink && (
                          <a href={colisTrackLink} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"><ExternalLink size={10} /></Button>
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input className="h-7 w-24 text-xs" placeholder="N° tracking"
                          value={colisTrackingData[pkgId]?.number ?? ''}
                          onChange={e => setColisTrackingData(prev => ({ ...prev, [pkgId]: { ...prev[pkgId], number: e.target.value } }))} />
                        <Input className="h-7 w-32 text-xs" placeholder="Lien (optionnel)"
                          value={colisTrackingData[pkgId]?.link ?? ''}
                          onChange={e => setColisTrackingData(prev => ({ ...prev, [pkgId]: { ...prev[pkgId], link: e.target.value } }))} />
                        <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => handleSaveColisTracking(pkgId)}>OK</Button>
                      </div>
                    )}
                    {/* Weight/Price - aligned with orders */}
                    {colisValidated ? (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px] gap-1"><CheckCircle size={10} /> {group.weight}kg — {(group.price || 0).toLocaleString()}đ</Badge>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.1" className="h-7 w-16 text-xs" placeholder="kg"
                          value={colisPackageData[pkgId]?.weight ?? ''}
                          onChange={e => setColisPackageData(prev => ({ ...prev, [pkgId]: { ...prev[pkgId], weight: e.target.value } }))} />
                        <Input type="number" step="1000" className="h-7 w-24 text-xs" placeholder="VND"
                          value={colisPackageData[pkgId]?.price ?? ''}
                          onChange={e => setColisPackageData(prev => ({ ...prev, [pkgId]: { ...prev[pkgId], price: e.target.value } }))} />
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-0.5" onClick={() => setConfirmValidateColis(pkgId)}>
                          <CheckCircle size={10} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Expanded child orders */}
                  {isExp && (
                    <div className="px-2 py-2 bg-white border-t border-blue-100 space-y-1">
                      {group.orders.map(o => <OrderRow key={o._id} o={o} isChild />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Address Dialog */}
        <Dialog open={!!addressDialog} onOpenChange={(open) => { if (!open) setAddressDialog(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('address')}</DialogTitle></DialogHeader>
            {addressDialog && (
              <div className="space-y-3 text-sm">
                <div><Label className="text-xs text-slate-500">{t('client')}</Label><p className="font-medium">{addressDialog.client_name} {addressDialog.client_firstname}</p></div>
                <div><Label className="text-xs text-slate-500">{t('address')}</Label><p className="font-medium">{addressDialog.client_address || '—'}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">{t('postal_code')}</Label><p className="font-medium">{addressDialog.client_postal_code || '—'}</p></div>
                  <div><Label className="text-xs text-slate-500">{t('city')}</Label><p className="font-medium">{addressDialog.client_city || '—'}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">{t('country')}</Label><p className="font-medium">{addressDialog.client_country}</p></div>
                  <div><Label className="text-xs text-slate-500">{t('phone')}</Label><p className="font-medium">{addressDialog.client_phone || '—'}</p></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Confirm validate order */}
        <AlertDialog open={!!confirmValidate} onOpenChange={(open) => { if (!open) setConfirmValidate(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-amber-700"><AlertTriangle size={24} /><AlertDialogTitle>{t('validate_confirm_title')}</AlertDialogTitle></div>
              <AlertDialogDescription>{t('transport_validate_warning')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction className="bg-emerald-700 hover:bg-emerald-800" onClick={handleValidatePackage}>{t('validate_definitive')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm validate colis */}
        <AlertDialog open={!!confirmValidateColis} onOpenChange={(open) => { if (!open) setConfirmValidateColis(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-amber-700"><AlertTriangle size={24} /><AlertDialogTitle>{t('validate_confirm_title')}</AlertDialogTitle></div>
              <AlertDialogDescription>{t('transport_validate_warning')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction className="bg-emerald-700 hover:bg-emerald-800" onClick={handleValidateColisPackage}>{t('validate_definitive')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
